import { datadogLogs } from '@datadog/browser-logs';

/**
 * 상단 크롬(GNB·투명 BTB) 높이를 담는 CSS 변수.
 * `usePublishElementHeight`가 측정값을 documentElement에 발행하고,
 * sticky 서브헤더·min-height 등이 `var()`로 직접 구독한다.
 * 기본값(측정 전)은 index.css의 `:root`에 반응형으로 선언되어 있다.
 */
export const GNB_HEIGHT_CSS_VAR = '--gnb-height';

/** 인라인 style·calc 표현에서 쓰는 `var(--gnb-height)` 문자열 */
export const GNB_HEIGHT = `var(${GNB_HEIGHT_CSS_VAR})`;

/** 모바일 GNB 기본 높이 (px) — index.css `:root` 기본값과 일치, JS fallback·SSR 초기값용 */
export const DEFAULT_MOBILE_NAVBAR_HEIGHT = 59;

/** PC GNB 기본 높이 (px) — index.css `:root` sm 기본값과 일치 */
export const DEFAULT_PC_NAVBAR_HEIGHT = 56;

/**
 * JS에서 숫자가 꼭 필요한 소수의 내부 소비자용(현재 useNavbarHeight) — `--gnb-height`를 px 숫자로 읽는다.
 * 스크롤 착지는 이 함수 대신 `.scroll-mt-gnb`/`scrollIntoViewBelowGnb`를 쓸 것. SSR에서는 null.
 */
export const readGnbHeightPx = (): number | null => {
  if (typeof window === 'undefined') return null;

  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(GNB_HEIGHT_CSS_VAR)
    .trim();
  const parsed = Number.parseInt(raw, 10);

  return Number.isNaN(parsed) ? null : parsed;
};

interface ScrollIntoViewBelowGnbParams {
  targetEl: Element | null | undefined;
  /** 상단 크롬 외에 추가로 확보할 여백(px) — 고정 서브 GNB·구분선 등 */
  extraOffsetPx: number;
}

/**
 * 동적으로 탐색한 요소(querySelector·children 등)로 상단 크롬에 가리지 않게 스크롤한다.
 * 대상에 `scroll-margin-top: calc(var(--gnb-height) + extra)`를 지정한 뒤 네이티브
 * `scrollIntoView`를 호출 — JS로 크롬 높이를 읽지 않는다.
 * 정적으로 소유한 요소는 이 유틸 대신 `.scroll-mt-gnb` 클래스를 붙일 것.
 * 주의: 대상에 인라인 `scrollMarginTop`이 영구 잔존하며, 이후 같은 요소에
 * 클래스 기반 `scroll-mt-*`를 붙여도 인라인 설정이 우선한다.
 */
export const scrollIntoViewBelowGnb = ({
  targetEl,
  extraOffsetPx,
}: ScrollIntoViewBelowGnbParams) => {
  // 동적 탐색이 빗나가면(id·DOM 구조 변경 등) 스크롤만 건너뛴다 — 검증 에러 안내 UI는 호출부가 별도 표시
  if (!targetEl) {
    datadogLogs.logger.error(
      '[scrollIntoViewBelowGnb] 스크롤 대상 요소를 찾지 못해 이동을 건너뜀',
    );
    return;
  }

  (targetEl as HTMLElement).style.scrollMarginTop =
    `calc(${GNB_HEIGHT} + ${extraOffsetPx}px)`;
  targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
